/**
 * Part grade (aka `part_type`) — one definition shared by the Lite wizard and
 * the detailed product wizard.
 *
 * A genuine OEM box, a branded aftermarket copy and a China copy are three
 * different products to a Bangladeshi buyer. Both wizards used to keep their
 * own copy of this list (and the detailed one silently defaulted every branded
 * listing to "aftermarket"), so the wording drifted and grades were wrong.
 */
export type PartGrade = "genuine_oem" | "aftermarket" | "india" | "china";

/** Empty string = "the seller has not chosen a grade yet". */
export type PartGradeValue = PartGrade | "";

export const PART_GRADE_OPTIONS: {
  value: PartGrade;
  en: string;
  bn: string;
  hint_en: string;
  hint_bn: string;
}[] = [
  {
    value: "genuine_oem",
    en: "Genuine OEM",
    bn: "জেনুইন OEM",
    hint_en: "Maker's own box",
    hint_bn: "কোম্পানির নিজস্ব বক্স",
  },
  {
    value: "aftermarket",
    en: "Aftermarket",
    bn: "আফটারমার্কেট",
    hint_en: "Branded copy",
    hint_bn: "ব্র্যান্ডেড কপি",
  },
  {
    value: "india",
    en: "India",
    bn: "ইন্ডিয়া",
    hint_en: "India-made copy",
    hint_bn: "ইন্ডিয়ায় তৈরি কপি",
  },
  {
    value: "china",
    en: "China",
    bn: "চায়না",
    hint_en: "Budget copy",
    hint_bn: "কম দামি কপি",
  },
];

/**
 * Grade a non-branded listing carries by default. Local / unbranded and used
 * parts are aftermarket by definition, so only branded parts must be asked.
 */
export const DEFAULT_GRADE_FOR_UNBRANDED: PartGrade = "aftermarket";

/** Every grade value, in display order. */
export const PART_GRADES: PartGrade[] = PART_GRADE_OPTIONS.map((o) => o.value);

/** Bilingual label for a stored grade; falls back to the raw value. */
export function partGradeLabel(value: string | null | undefined, lang: "en" | "bn" = "en"): string {
  const opt = PART_GRADE_OPTIONS.find((o) => o.value === value);
  if (!opt) return value ?? "";
  return lang === "bn" ? opt.bn : opt.en;
}

/**
 * Free text (CSV import, AI suggestion, scanned box) mapped onto the enum.
 * Returns null when the text names no grade we know.
 */
export function normalizePartGrade(raw: string | null | undefined): PartGrade | null {
  const v = (raw ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (!v) return null;
  if (["genuineoem", "genuine", "oem", "original", "new"].includes(v)) return "genuine_oem";
  if (["aftermarket", "branded", "brand"].includes(v)) return "aftermarket";
  if (["india", "indian", "bharat", "indianmade", "in"].includes(v)) return "india";
  if (["china", "chinese", "chaina", "cn"].includes(v)) return "china";
  return null;
}
